import { Provider } from '@angular/core';
import {
  ApolloClientOptions,
  InMemoryCache,
  NormalizedCacheObject,
  split,
  StoreObject,
} from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '@env/environment';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { createClient } from 'graphql-ws';
import { Kind, OperationTypeNode } from 'graphql/language';
import { lastValueFrom } from 'rxjs';

import { onError } from '@apollo/client/link/error';
import { ErrorService } from '@shared/services';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';

const generateUniqueId = (object: Readonly<StoreObject>): string => {
  return (
    (object['cacheKey'] ?? object['id'])
      ? `${object.__typename}:${object['id'] as string}`
      : null
  ) as string;
};

const createErrorLink = (errorService: ErrorService) => {
  return onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(error => {
        console.error(`[GraphQL error]: ${error.message}`);
        errorService.openDialog({
          type: 'graphql',
          error,
          location: window.location.href,
        });
      });
    }
    if (networkError) {
      console.error(`[Network error]: ${networkError.message}`);
      errorService.openDialog({
        type: 'network',
        error: networkError,
        location: window.location.href,
      });
    }
  });
};

const createHttpLink = (httpLink: HttpLink) => {
  return httpLink.create({
    uri: environment.HTTP_GRAPHQL_ENDPOINT,
    extractFiles: body => extractFiles(body, isExtractableFile),
  });
};

const createWsLink = (authService: AuthService): GraphQLWsLink => {
  return new GraphQLWsLink(
    createClient({
      url: environment.WS_GRAPHQL_ENDPOINT,
      lazy: true,
      connectionParams: async () => {
        const authToken = await lastValueFrom(
          authService.getAccessTokenSilently()
        );
        return { authToken };
      },
    })
  );
};

const createApolloLinks = (
  httpLink: HttpLink,
  authService: AuthService,
  errorService: ErrorService
): ApolloClientOptions<NormalizedCacheObject> => {
  const ws = createWsLink(authService);
  const http = createHttpLink(httpLink);
  const error = createErrorLink(errorService);

  const link = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === Kind.OPERATION_DEFINITION &&
        definition.operation === OperationTypeNode.SUBSCRIPTION
      );
    },
    ws,
    http
  );

  return {
    link: error.concat(link),
    cache: new InMemoryCache({ dataIdFromObject: generateUniqueId }),
  };
};

export const graphqlProviders: Provider[] = [
  {
    provide: APOLLO_OPTIONS,
    useFactory: createApolloLinks,
    deps: [HttpLink, AuthService, ErrorService],
  },
  Apollo,
];
